// packages/chatbot/src/components/ToolResultBubble.tsx
import { useMemo, type ReactNode } from 'react';
import { Card, IconButton } from '@dopomogai/ui';
import {
  CheckCircle,
  AlertCircle,
  Copy,
  Wrench,
  Play,
  type LucideIcon,
} from 'lucide-react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { a11yLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import type { ToolReturn } from '../types';
import clsx from 'clsx';

interface ToolResultBubbleProps {
  message: ToolReturn;
  agentAvatar?: ReactNode | string;
  onReplayFrontendActions?: (toolCallId: string) => void;
}

type ToolResultStatus = 'success' | 'error' | 'info';

function dedupeStrings(lines: string[] | null | undefined): string[] | null {
  if (!lines || lines.length === 0) return null;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const l of lines) {
    if (seen.has(l)) continue;
    seen.add(l);
    out.push(l);
  }
  return out;
}

export function ToolResultBubble({ message, agentAvatar, onReplayFrontendActions }: ToolResultBubbleProps) {
  const { isJson, status, contentString } = useMemo(() => {
    let parsedContent: any;
    let isContentJson = false;
    let detectedStatus: string = 'info';

    if (typeof message.output === 'string') {
      try {
        parsedContent = JSON.parse(message.output);
        isContentJson = true;
      } catch {
        parsedContent = message.output;
      }
    } else {
      parsedContent = message.output;
      isContentJson = typeof parsedContent === 'object' && parsedContent !== null;
    }

    if (isContentJson && parsedContent.status) {
      detectedStatus = String(parsedContent.status).toLowerCase();
    }

    const stringifiedContent = isContentJson
      ? JSON.stringify(parsedContent, null, 2)
      : String(parsedContent);

    const finalStatus: ToolResultStatus =
      detectedStatus === 'success' || detectedStatus === 'error' ? (detectedStatus as ToolResultStatus) : 'info';

    return { isJson: isContentJson, status: finalStatus, contentString: stringifiedContent };
  }, [message.output]);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(contentString)
      .catch(() => {});
  };

  const statusConfig: Record<ToolResultStatus, { Icon: LucideIcon; colorClasses: string; iconColor: string }> = {
    success: {
      Icon: CheckCircle,
      colorClasses: 'text-emerald-900 bg-emerald-50 border-emerald-400',
      iconColor: 'text-emerald-700',
    },
    error: {
      Icon: AlertCircle,
      colorClasses: 'text-red-900 bg-red-50 border-red-400',
      iconColor: 'text-red-700',
    },
    info: { Icon: Wrench, colorClasses: 'text-sky-900 bg-sky-50 border-sky-400', iconColor: 'text-sky-700' },
  };

  const { Icon, colorClasses, iconColor } = statusConfig[status];

  const renderAvatar = () => {
    if (typeof agentAvatar === 'string') {
      return <img src={agentAvatar} alt="Agent" className="w-8 h-8 rounded-lg object-cover" />;
    }
    const bgColor = colorClasses.split(' ').find((cls) => cls.startsWith('bg-')) || 'bg-slate-400';
    return (
      agentAvatar || (
        <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', bgColor)}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      )
    );
  };

  // Concise summary for frontend_actions results
  const simpleFrontendLines = useMemo(() => {
    if (message.tool_name !== 'frontend_actions') return null;
    try {
      const parsed = JSON.parse(contentString);
      const rs = Array.isArray(parsed?.results) ? parsed.results : [];
      const lines: string[] = [];
      for (const r of rs) {
        const name = r?.name;
        if (name === 'navigate' && r?.result)
          lines.push(`Navigated to ${(r.result.path || r.result.url || '') || 'page'}`);
        else if (name === 'open_section') lines.push(`Opened section #${r?.result?.id || ''}`.trim());
        else if (name === 'select_preset') lines.push(`Selected preset`);
        else if (name === 'toggle_feature') lines.push(`Toggled a feature`);
        else if (name === 'set_copy') lines.push(`Updated copy`);
        else if (name === 'submit_lead_email') lines.push(`Submitted email`);
        else if (name === 'fill_field') lines.push(`Filled a form field`);
        else if (name === 'generate_quote') lines.push(`Generated a quote`);
      }
      const deduped = dedupeStrings(lines) || ['Completed frontend action(s)'];
      return deduped;
    } catch {
      return ['Completed frontend action(s)'];
    }
  }, [message.tool_name, contentString]);

  const replayEnabled = Boolean(onReplayFrontendActions && message.tool_name === 'frontend_actions');

  return (
    <div className="flex gap-3 w-full justify-start">
      {renderAvatar()}
      <div className="flex flex-col gap-1 max-w-[85%] items-start w-full">
        <Card className={clsx('w-full border-2 shadow-sm cursor-default select-text', colorClasses)} noPadding>
          <header
            className="flex items-center justify-between p-3"
            role="heading"
            aria-label={`Tool Result: ${message.tool_name}`}
          >
            <div className="flex items-center gap-2">
              <Icon className={clsx('w-4 h-4 shrink-0', iconColor)} />
              <span className="font-semibold text-sm">Tool Result: {message.tool_name} ({status})</span>
            </div>
            <div className="flex items-center gap-1">
              {replayEnabled && (
                <IconButton
                  icon={<Play className="w-3.5 h-3.5" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onReplayFrontendActions!(message.tool_call_id);
                  }}
                  ariaLabel="Replay this action"
                  variant="ghost"
                  size="sm"
                />
              )}
              <IconButton
                icon={<Copy className="w-3.5 h-3.5" />}
                onClick={handleCopy}
                ariaLabel="Copy tool result"
                variant="ghost"
                size="sm"
              />
            </div>
          </header>

          {message.tool_name === 'frontend_actions' && simpleFrontendLines && (
            <div className="px-3 pb-3">
              <ul className="list-disc list-inside text-sm text-slate-800">
                {simpleFrontendLines.map((l, i) => (
                  <li key={`${l}-${i}`} title="frontend_actions result">
                    {l}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <details className="px-3 pb-3 text-xs text-slate-500 cursor-pointer">
            <summary className="select-none outline-none">View raw output</summary>
            <div className="mt-2">
                <SyntaxHighlighter
                    language={isJson ? 'json' : 'text'}
                    style={a11yLight}
                    customStyle={{
                      margin: 0,
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E2E8F0',
                      fontSize: '0.8rem',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      lineHeight: '1.4',
                    }}
                    PreTag="div"
                    codeTagProps={{ style: { fontFamily: 'inherit' } }}
                >
                    {contentString}
                </SyntaxHighlighter>
            </div>
          </details>
        </Card>
      </div>
    </div>
  );
}
